import { Tooltip } from '@mantine/core'

import { AutomatedRuleActionType } from 'src/graphql/types/automatedRules'
import { buildFullName } from 'src/lib/formatters'

import { ACTION_TYPE_LABEL } from '../../utils/actionDisplay'
import ActionIconBadge from '../atoms/ActionIconBadge'

type EmployeeLike = {
  firstName?: string
  lastName?: string
}

type ActionLike = {
  actionType: string
  textTemplate?: { title: string } | null
  emailTemplate?: { title: string } | null
  documentTemplatePackage?: { packageName: string } | null
  taskTemplate?: { title: string } | null
  noteTemplate?: { title: string } | null
  training?: { name: string } | null
  course?: { name: string } | null
  package?: { name: string } | null
  leadEmployee?: EmployeeLike | null
  memberEmployees?: EmployeeLike[]
}

const detailFor = (action: ActionLike): string | null => {
  if (action.actionType === AutomatedRuleActionType.REASSIGN_LEAD_EMPLOYEE) {
    return (action.leadEmployee && buildFullName(action.leadEmployee)) || null
  }

  if (action.actionType === AutomatedRuleActionType.ADD_MEMBER_EMPLOYEE) {
    const names = (action.memberEmployees ?? [])
      .map(buildFullName)
      .filter((name): name is string => Boolean(name))
    return names.length ? names.join(', ') : null
  }

  return (
    action.textTemplate?.title ||
    action.emailTemplate?.title ||
    action.documentTemplatePackage?.packageName ||
    action.taskTemplate?.title ||
    action.noteTemplate?.title ||
    action.training?.name ||
    action.course?.name ||
    action.package?.name ||
    null
  )
}

const ActionRow = ({ action }: { action: ActionLike }) => {
  const title = ACTION_TYPE_LABEL[action.actionType] ?? action.actionType
  const detail = detailFor(action)

  const tooltipLabel = (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold">{title}</span>
      {detail ? (
        <span className="text-doubleNickel-gray-200">{detail}</span>
      ) : null}
    </div>
  )

  return (
    <Tooltip
      label={tooltipLabel}
      withArrow
      withinPortal
      position="top"
      multiline
      maw={280}
    >
      <div className="grid cursor-default grid-cols-[22px_minmax(0,1fr)] items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-doubleNickel-gray-50">
        <ActionIconBadge action={action} />
        <div className="min-w-0">
          <div className="truncate text-xs font-medium leading-tight text-doubleNickel-gray-900">
            {title}
          </div>
          {detail ? (
            <div className="mt-px truncate text-[10px] font-normal leading-tight text-doubleNickel-gray-500">
              {detail}
            </div>
          ) : null}
        </div>
      </div>
    </Tooltip>
  )
}

export default ActionRow
